import React, { useState, forwardRef, useImperativeHandle } from "react";
import {
  getHostLogoStyleByHostId,
  mediaStatusToTextAndColor,
  getSocialChannelImage,
  THUMBNAIL_SIZE_SUFFIX,
} from "../../utils";
import { MediaModel } from "@/database";
import { formatTimestampToAgo, secondsToHms } from "../../utils";
import { Button, Modal, PublishedIcon } from "..";
import noImage from "../../assets/icons/svg/no-image.svg";
import { updateModal } from "@/store/app/app.slice";
import { useAppSelector } from "@/store/index";
import { RefreshCw } from "lucide-react";

const options = [
  { label: "Legal Obligation", value: "Legal Obligation" },
  { label: "Duplicate content", value: "Duplicate content" },
  { label: "Uploaded by mistake", value: "Uploaded by mistake" },
  { label: "Agreement expired", value: "Agreement expired" },
  { label: "Outdated content", value: "Outdated content" },
  { label: "Embed disabled (3rd party)", value: "Embed disabled (3rd party)" },
  { label: "Other", value: "Other" },
];

export interface MediaDeleteRef {
  handleSubmit: () => Promise<boolean>;
}

interface MediaDeleteProps {
  media: MediaModel;
  onDelete?: (data: {
    id: string;
    reason: string;
    notes?: string;
    socials?: string[];
  }) => void;
  onClose?: () => void;
  actionType?: "delete" | "archive";
  onFormSubmit?: (formData: {
    id: string;
    reason: string;
    notes?: string;
    socials: string[];
  }) => void;
  isOpen?: boolean;
  hideSocials?: boolean;
  noteText?: string;
  /** Passed through to the internal Modal - e.g. to render above an Offcanvas
   * sheet this dialog was opened from (see BasePublishCard's delete confirm). */
  zIndex?: number;
}

const MediaDelete = forwardRef<MediaDeleteRef, MediaDeleteProps>(
  (
    {
      media,
      onDelete,
      onClose,
      actionType = "delete",
      onFormSubmit,
      isOpen = false,
      hideSocials = false,
      noteText,
      zIndex,
    },
    ref
  ) => {
    const [notes, setNotes] = useState<string>("");
    const [selectedReason, setSelectedReason] = useState<string>("");
    const [errors, setErrors] = useState<{ reason?: string; notes?: string }>(
      {}
    );
    const [selectedSocials, setSelectedSocials] = useState<string[]>([]);
    const [captcha, setCaptcha] = useState("");
    const [captchaText, setCaptchaText] = useState("");
    const [captchaError, setCaptchaError] = useState("");
    const socialChannelsList = useAppSelector(
      (s) => s.app.userSocialChannels.socialChannelsList
    );

    const handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setNotes(event.target.value);
      if (errors.notes) {
        setErrors((prev) => ({ ...prev, notes: undefined }));
      }
    };

    const handleReasonChange = (selectedOption: any) => {
      const value = selectedOption?.value || "";
      setSelectedReason(value);
      if (errors.reason) {
        setErrors((prev) => ({ ...prev, reason: undefined }));
      }
      if (value !== "Other") {
        setNotes("");
      }
    };

    const handleSocialCheckboxChange = (socialId: string) => {
      setSelectedSocials((prev) => {
        if (prev.includes(socialId)) {
          return prev.filter((id) => id !== socialId);
        } else {
          return [...prev, socialId];
        }
      });
    };

    const deletableSocials = media.socials.filter(
      (social) => social.status === 10
    );

    const handleSelectAllSocials = () => {
      if (selectedSocials.length === deletableSocials.length) {
        setSelectedSocials([]);
      } else {
        setSelectedSocials(deletableSocials.map((social) => social.id));
      }
    };

    const getSocialHostLogoStyle = (social: any) => {
      if (!social?.host) return null;
      const style = getHostLogoStyleByHostId(social.host);
      return style && Object.keys(style).length > 0 ? style : null;
    };

    const handleImageError = (
      event: React.SyntheticEvent<HTMLImageElement>
    ) => {
      event.currentTarget.src = noImage;
    };

    const generateCaptcha = () => {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let result = "";
      for (let i = 0; i < 6; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      setCaptchaText(result);
      setCaptcha("");
      setCaptchaError("");
    };

    // Generate captcha when modal opens
    React.useEffect(() => {
      if (isOpen) {
        generateCaptcha();
      }
    }, [isOpen]);

    const validateForm = () => {
      const newErrors: { reason?: string; notes?: string } = {};

      if (actionType === "delete") {
        if (!selectedReason) {
          newErrors.reason = "Please select a reason for deletion";
        }

        if (selectedReason === "Other" && !notes.trim()) {
          newErrors.notes = 'Notes are required when "Other" is selected';
        }
      }

      setErrors(newErrors);

      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (): Promise<boolean> => {
      if (!validateForm()) {
        return false;
      }

      // Validate captcha
      if (!captcha) {
        setCaptchaError("Please enter the captcha");
        return false;
      }
      if (captcha.toUpperCase() !== captchaText) {
        setCaptchaError("Invalid captcha");
        return false;
      }

      try {
        if (actionType === "delete") {
          const deleteData = {
            id: media.id,
            reason: selectedReason,
            socials: selectedSocials,
            ...(selectedReason === "Other" && { notes: notes }),
          };

          if (onFormSubmit) {
            onFormSubmit(deleteData);
          } else if (onDelete) {
            await onDelete(deleteData);
          }
        } else {
          if (onFormSubmit) {
            onFormSubmit({
              id: media.id,
              reason: "",
              socials: selectedSocials,
            });
          } else if (onDelete) {
            await onDelete({
              id: media.id,
              reason: "",
              socials: selectedSocials,
            });
          }
        }

        if (onClose) {
          onClose();
        }
        return true;
      } catch (error) {
        console.error(`Error ${actionType}ing media:`, error);
        alert(`Failed to ${actionType} content. Please try again.`);
        return false;
      }
    };

    useImperativeHandle(ref, () => ({
      handleSubmit,
    }));

    const content = (
      <div>
        <h6 className="text-danger">
          Are you sure you want to {actionType} the content?
        </h6>
        <div className="mt-3 line-height-1">
          <span style={{ fontWeight: "bold" }}>Note:</span>
          {noteText ||
            "Deleting this content may impact other related content, potentially causing inconsistencies or errors. Proceed with caution and review any dependencies before making changes."}
        </div>
        <div className="mt-2">
          <span className="fw-bold">{media.title}</span>
        </div>
        <div className="d-flex align-items-center gap-2 mt-2">
          <div>
            <span
              className={`badge ${
                mediaStatusToTextAndColor(media.media_status || 0).statusColor
              }`}
            >
              {mediaStatusToTextAndColor(media.media_status || 0).statusText}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2 ">
            <label>Duration:</label>
            <span> {secondsToHms(media.meta?.duration)} </span> |{" "}
            <label>Views:</label>
            <span> {media.views?.views} </span> | <label>Published:</label>
            <span>
              {" "}
              {formatTimestampToAgo(media.mtuser || media.mtbydata?.mt)}{" "}
            </span>
          </div>
        </div>
        {!hideSocials && deletableSocials.length > 0 && (
          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between">
              <span className="fw-bold">
                WHICH PLATFORMS WOULD YOU LIKE TO REMOVE THIS VIDEO FROM?
              </span>
              <button
                className="btn btn-sm btn-outline-primary border-0"
                onClick={handleSelectAllSocials}
              >
                Select All
              </button>
            </div>
            <div className="mt-3">
              <div className="d-flex flex-column gap-2">
                {deletableSocials.map((social) => {
                  const hostLogoStyle = getSocialHostLogoStyle(social);
                  const channelImg = getSocialChannelImage(
                    social,
                    socialChannelsList,
                    { sizeSuffix: THUMBNAIL_SIZE_SUFFIX }
                  );
                  return (
                    <div
                      key={social.id}
                      className="d-flex align-items-center gap-2 p-2 border rounded"
                    >
                      <input
                        type="checkbox"
                        onChange={() => handleSocialCheckboxChange(social.id)}
                        checked={selectedSocials.includes(social.id)}
                      />
                      <div className="d-flex align-items-center gap-2">
                        <div className="position-relative">
                          {hostLogoStyle ? (
                            <div
                              style={{
                                ...hostLogoStyle,
                                width: "30px",
                                height: "30px",
                                borderRadius: "50%",
                              }}
                            />
                          ) : (
                            <img
                              src={channelImg || noImage}
                              alt={social.channel_name || social.job_type}
                              onError={handleImageError}
                              style={{
                                width: "30px",
                                height: "30px",
                                borderRadius: "50%",
                              }}
                            />
                          )}
                          <div
                            style={{
                              position: "absolute",
                              bottom: -2,
                              right: -2,
                              backgroundColor: "white",
                              borderRadius: "2px 0 0 0",
                              padding: "1px",
                            }}
                          >
                            <PublishedIcon job_type={social?.job_type} />
                          </div>
                        </div>
                        <span className="fw-medium">
                          {social?.channel_name}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <div className="d-flex flex-column  mt-3 p-1 rounded-3 align-items-start w-100">
          {actionType === "delete" && (
            <>
              <label>Reason for deletion</label>
              <div className="d-flex flex-row gap-3 w-100">
                <>
                  <select
                    className={`form-select form-select-sm ${
                      errors.reason ? "border-danger" : ""
                    }`}
                    value={selectedReason}
                    onChange={(e) =>
                      handleReasonChange({ value: e.target.value })
                    }
                    style={{ maxWidth: "300px", height: "36px" }}
                  >
                    <option value="">Select Reason</option>
                    {options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.reason && (
                    <small className="text-danger d-block mt-1">
                      {errors.reason}
                    </small>
                  )}
                </>
                {selectedReason === "Other" && (
                  <>
                    <input
                      type="text"
                      className={`form-control form-control-sm ${
                        errors.notes ? "border-danger" : ""
                      }`}
                      placeholder="Notes"
                      value={notes}
                      onChange={handleNotesChange}
                    />
                    {errors.notes && (
                      <small className="text-danger d-block mt-1">
                        {errors.notes}
                      </small>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );

    // If showModal is true, wrap content in modal
    if (isOpen) {
      return (
        <Modal
          isOpen={isOpen}
          onClose={onClose || (() => undefined)}
          title={`${actionType === "delete" ? "Delete" : "Inactive"} Media`}
          width="800px"
          zIndex={zIndex}
          updateModal={updateModal}
          footer={
            <div className="d-flex flex-column gap-2 w-100">
              {captchaError && (
                <div className="text-danger small mb-2">{captchaError}</div>
              )}
              <div className="d-flex justify-content-between w-100">
                <div className="d-flex gap-3 align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="px-3 py-2 bg-white border rounded text-center font-monospace fw-bold"
                      style={{
                        minWidth: "100px",
                        letterSpacing: "2px",
                        backgroundColor: "#f8f9fa",
                        border: "2px solid #dee2e6",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        MozUserSelect: "none",
                      }}
                      onCopy={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                    >
                      {captchaText || "Loading..."}
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={generateCaptcha}
                    >
                      <RefreshCw size={16} />
                    </button>
                  </div>
                  <div style={{ minWidth: "150px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Type the captcha (no copy-paste)"
                      value={captcha}
                      onChange={(e) => {
                        setCaptcha(e.target.value);
                        if (captchaError) {
                          setCaptchaError("");
                        }
                      }}
                      onPaste={(e) => e.preventDefault()}
                      onCopy={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    type="outline-primary"
                    className="border-0"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="danger"
                    onClick={async () => {
                      const formValid = await handleSubmit();
                      if (formValid && onClose) {
                        onClose();
                      }
                    }}
                  >
                    {actionType === "delete" ? "Delete" : "Inactive"}
                  </Button>
                </div>
              </div>
            </div>
          }
        >
          {content}
        </Modal>
      );
    }

    // Return just the content if not showing modal
    return content;
  }
);

MediaDelete.displayName = "MediaDelete";

export default MediaDelete;
